import { useEffect, useRef } from "react";
import { Input, Select } from "@mantine/core";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { majorCities } from "../helpers/GetAQuoteHelpers";
import { MarkerOutline } from "../icons";

export default function AddressInput({
  index,
  location,
  formData,
  updateLocation,
  locationType,
  updateFormField,
  useCityPicker = false,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const indexRef = useRef(index);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    if (!useCityPicker && inputRef.current && location?.address) {
      inputRef.current.value = location.label;
    }
  }, [location, useCityPicker]);

  const cities = majorCities.map((c) => ({
    label: `${c.city}, ${c.region} — ${c.country}`,
    value: `${c.city}, ${c.region} — ${c.country}`,
    query: `${c.city}, ${c.region}, ${c.country}`,
  }));

  useEffect(() => {
    async function initGoogle() {
      setOptions({
        key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        version: "weekly",
      });

      const { Autocomplete } = await importLibrary("places");
      const { Geocoder } = await importLibrary("geocoding");

      geocoderRef.current = new Geocoder();

      if (!useCityPicker && inputRef.current) {
        const autocomplete = new Autocomplete(inputRef.current, {
          fields: [
            "formatted_address",
            "geometry",
            "name",
            "place_id",
            "address_components",
          ],
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (!place.geometry) {
            console.log("No geometry available");
            return;
          }

          processPlace(place);
        });

        autocompleteRef.current = autocomplete;
      }
    }

    initGoogle();

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [useCityPicker]);

  function processPlace(place) {
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const formatted_address = place.formatted_address;
    const placeId = place.place_id;
    const name = place.name || formatted_address;

    const components = place.address_components || [];

    const street_number =
      components.find((c) => c.types.includes("street_number"))?.long_name ||
      "";

    const route =
      components.find((c) => c.types.includes("route"))?.long_name || "";

    const street_1 = street_number && route ? `${street_number} ${route}` : "";

    const city =
      components.find((c) => c.types.includes("locality"))?.long_name || "";

    const state =
      components.find((c) => c.types.includes("administrative_area_level_1"))
        ?.short_name || "";

    const postalcode =
      components.find((c) => c.types.includes("postal_code"))?.long_name || "";

    const googleMapsUrl = `https://maps.google.com/?q=${encodeURIComponent(
      formatted_address,
    )}&ftid=${placeId}`;

    const fullAddress = [street_1, city, state].filter(Boolean).join(", ");

    updateLocation(indexRef.current, {
      label: `${name}, ${formatted_address}`,
      address: fullAddress,
      lat,
      lng,
      placeId,
      locationType,
      name,
      street_1,
      street_2: "",
      city,
      state,
      postalcode,
      formatted_address,
      formatted_phone: "",
      description: name,
      url: googleMapsUrl,
      utc_offset: -300,
    });
  }

  function geocodeCity(query) {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ address: query }, (results, status) => {
      if (status !== "OK" || !results[0]) {
        console.log("Geocode failed:", status);
        return;
      }

      const place = results[0];

      if (inputRef.current) {
        inputRef.current.value = place.formatted_address;
      }

      processPlace(place);
    });
  }

  function handleCitySelect(value) {
    if (!value) return;

    const city = cities.find((c) => c.value === value);
    if (!city) return;

    if (updateFormField) {
      updateFormField("city", city.value);
    }

    geocodeCity(city.query);
  }

  return (
    <>
      {useCityPicker ? (
        <Select
          withAsterisk
          label="Select City"
          w={"100%"}
          p={8}
          placeholder="Select city"
          searchable
          data={cities}
          value={formData.city || null}
          onChange={handleCitySelect}
          leftSection={
            <MarkerOutline
              style={{ color: "#646464" }}
              width={10}
              height={10}
            />
          }
          comboboxProps={{
            withinPortal: true,
            portalProps: { target: "#get-a-quote" },
          }}
        />
      ) : (
        <Input
          ref={inputRef}
          placeholder="Address"
          leftSection={
            <MarkerOutline
              style={{ color: "#646464" }}
              width={10}
              height={10}
            />
          }
        />
      )}
    </>
  );
}
